import bact from '@/sui/data/sheets/edit/bact'
import pneu from '@/sui/data/sheets/edit/pneu'
import patient from '@/sui/data/sheets/edit/patient'
import intub from '@/sui/data/sheets/edit/intub'
import multi from '@/sui/data/sheets/edit/multi'
import tools from '@/sui/data/sheets/edit/tools'
import notes from '@/sui/data/sheets/edit/notes/expADI'
import fields from '@/sui/data/sheets/edit/fields'

const select = {
  sepNeoOrNeoAndMix: tools.oneTrue(multi.select.sep, patient.select.isNeoOrNeoMix),
  reaNotNeo: tools.allTrue(patient.select.isRea, patient.select.isAdPedOrPedMix),
  sepNeoOrRea: tools.oneTrue(multi.select.sep, patient.select.isRea),
  sepNeoOrNeoAndMixRea: tools.oneTrue(
    multi.select.sep,
    tools.allTrue(
      patient.select.isRea,
      patient.select.isNeoOrNeoMix
    )
  ),
  bactAdPed: tools.allTrue(patient.select.isAdPedOrPedMix, multi.select.bact)
}

const titles = [
  {
    text: 'DONNÉES ADMINISTRATIVES DU PATIENT',
    start: fields.uf
  },
  {
    text: `DEVENIR DU PATIENT 7 JOURS APRÈS LA DATE DE L'ÉPISODE`,
    start: fields.dead
  }
]

const repeatableTitles = [
  {
    text: 'DONNÉES RELATIVES À LA BACTÉRIÉMIE ASSOCIÉE AUX SOINS',
    start: `${fields.episod}`
  },
  {
    text: 'DONNÉES RELATIVES AU SEPSIS',
    start: `sep${fields.episod}`
  },
  {
    text: `DONNÉES RELATIVES À L'EXPOSITION AUX DISPOSITIFS INVASIFS`,
    start: fields.ecmo
  },
  {
    text: 'DONNEÉS RELATIVES À LA PNEUMOPATHIE ASSOCIÉE AUX SOINS',
    start: `pneu${fields.episod}`
  }
]

const sepUnitType = tools.select(patient.unitType.v1.ro, multi.select.sep)
sepUnitType.field = `sep${sepUnitType.field}`

const common = {
  uf: tools.select(patient.ufs.expADI, multi.select.notSep),
  ufSep: tools.select(patient.ufs.expADISep, multi.select.sep),
  unitType: {
    v1: tools.select(patient.unitType.v1.ro, multi.select.notSep),
    v2: tools.select(patient.unitType.v2.ro, multi.select.notSep)
  },
  immuno: tools.select(patient.immuno, multi.select.notSep),
  cancer: tools.select(patient.cancer, multi.select.notSep),
  mix: tools.andSelect(patient.mixManagment, multi.select.notSep),
  origin: {
    v1: tools.select(patient.patientOrigin.v2, patient.select.isAdPedOrPedMix),
    v2: tools.select(patient.patientOrigin.v3, patient.select.isAdPedOrPedMix)
  },
  neoOrigin: tools.select(patient.neoOrigin, select.sepNeoOrNeoAndMix),
  birthSite: tools.select(patient.birthSite, select.sepNeoOrNeoAndMix),
  birthWeight: tools.select(patient.birthWeight, select.sepNeoOrNeoAndMix),
  gestationalAge: tools.select(patient.gestationalAge, select.sepNeoOrNeoAndMix),
  neoVentilation: tools.select(patient.neoVentilation, select.sepNeoOrNeoAndMix),
  leucomalacy: tools.select(patient.leucomalacy, select.sepNeoOrNeoAndMix),
  trauma: tools.select(patient.trauma, select.reaNotNeo),
  diagnosis: tools.select(patient.diagnosis, select.reaNotNeo),
  igsII: tools.andSelect(patient.igsII, patient.select.isRea),
  pimII: tools.andSelect(patient.pimII.mix, select.sepNeoOrRea),
  cribII: tools.select(patient.cribII.mix, select.sepNeoOrNeoAndMixRea),
  otherSeverity: tools.andSelect(patient.otherSeverity, select.sepNeoOrRea),
  otherSeverityScore: tools.andSelect(patient.otherSeverityScore, select.sepNeoOrRea),
  ecmo: tools.select(intub.ecmo, multi.select.pneu),
  intubated: tools.select(intub.intubated.std, multi.select.pneu),
  intubStart: tools.andSelect(intub.intubStart.std, multi.select.pneu),
  reintubated: tools.andSelect(intub.reintubated.std, multi.select.pneu),
  intubEnd: tools.andSelect(intub.intubEnd.std, multi.select.pneu),
  bmi: tools.select(patient.bmi, select.bactAdPed)
}

const expFields = {
  v1: [
    multi.expManagment,
    patient.sheetNumber.v2,
    common.uf,
    common.ufSep,
    common.unitType.v1,
    sepUnitType,
    patient.birthdate,
    patient.sex,
    patient.entrydate,
    common.immuno,
    common.cancer,
    patient.antibio,
    patient.portageBmrBhre,
    patient.bmrBhreMicroOrg.v2,
    common.mix,
    common.origin.v1,
    common.neoOrigin,
    common.birthSite,
    common.birthWeight,
    common.gestationalAge,
    common.neoVentilation,
    common.leucomalacy,
    common.trauma,
    common.diagnosis,
    common.igsII,
    common.pimII,
    common.cribII,
    common.otherSeverity,
    common.otherSeverityScore,
    patient.dead,
    patient.deathDate,
    common.ecmo,
    common.intubated,
    common.intubStart,
    common.reintubated,
    common.intubEnd,
    ...bact.all.v2.exp,
    ...pneu.all.exp.v1,
    ...bact.sep.v1
  ],

  v2: [
    multi.expManagment,
    patient.sheetNumber.v2,
    common.uf,
    common.ufSep,
    common.unitType.v1,
    sepUnitType,
    patient.birthdate,
    patient.sex,
    patient.entrydate,
    common.immuno,
    common.cancer,
    patient.antibio,
    patient.portageBmrBhre,
    patient.bmrBhreMicroOrg.v2,
    common.mix,
    common.origin.v2,
    common.neoOrigin,
    common.birthSite,
    common.birthWeight,
    common.gestationalAge,
    common.neoVentilation,
    common.leucomalacy,
    common.trauma,
    common.diagnosis,
    common.igsII,
    common.pimII,
    common.cribII,
    common.otherSeverity,
    common.otherSeverityScore,
    patient.dead,
    patient.deathDate,
    common.ecmo,
    common.intubated,
    common.intubStart,
    common.reintubated,
    common.intubEnd,
    ...bact.all.v3.exp,
    ...pneu.all.exp.v1,
    ...bact.sep.v2
  ],

  v3: [
    multi.expManagment,
    patient.sheetNumber.v2,
    common.uf,
    common.ufSep,
    common.unitType.v1,
    sepUnitType,
    patient.birthdate,
    patient.sex,
    patient.entrydate,
    common.immuno,
    common.cancer,
    patient.antibio,
    patient.portageBmrBhre,
    patient.bmrBhreMicroOrg.v2,
    common.mix,
    common.origin.v2,
    common.neoOrigin,
    common.birthSite,
    common.birthWeight,
    common.gestationalAge,
    common.neoVentilation,
    common.leucomalacy,
    common.trauma,
    common.diagnosis,
    common.igsII,
    common.pimII,
    common.cribII,
    common.otherSeverity,
    common.otherSeverityScore,
    patient.dead,
    patient.deathDate,
    common.ecmo,
    common.intubated,
    common.intubStart,
    common.reintubated,
    common.intubEnd,
    ...bact.all.v4.exp,
    ...pneu.all.exp.v1,
    ...bact.sep.v3
  ],

  v4: [
    multi.expManagment,
    patient.sheetNumber.v2,
    common.uf,
    common.ufSep,
    common.unitType.v1,
    sepUnitType,
    patient.birthdate,
    patient.sex,
    patient.entrydate,
    common.immuno,
    common.cancer,
    patient.antibio,
    patient.portageBmrBhre,
    patient.bmrBhreMicroOrg.v2,
    common.mix,
    common.origin.v2,
    common.bmi,
    common.neoOrigin,
    common.birthSite,
    common.birthWeight,
    common.gestationalAge,
    common.neoVentilation,
    common.leucomalacy,
    common.trauma,
    common.diagnosis,
    common.igsII,
    common.pimII,
    common.cribII,
    common.otherSeverity,
    common.otherSeverityScore,
    patient.dead,
    patient.deathDate,
    common.ecmo,
    common.intubated,
    common.intubStart,
    common.reintubated,
    common.intubEnd,
    ...bact.all.v5.exp,
    ...pneu.all.exp.v2,
    ...bact.sep.v3
  ],

  v5: [
    multi.expManagment,
    patient.sheetNumber.v2,
    common.uf,
    common.ufSep,
    common.unitType.v2,
    sepUnitType,
    patient.birthdate,
    patient.sex,
    patient.entrydate,
    common.immuno,
    common.cancer,
    patient.antibio,
    patient.portageBmrBhre,
    patient.bmrBhreMicroOrg.v2,
    common.mix,
    common.origin.v2,
    common.bmi,
    common.neoOrigin,
    common.birthSite,
    common.birthWeight,
    common.gestationalAge,
    common.neoVentilation,
    common.leucomalacy,
    common.trauma,
    common.diagnosis,
    common.igsII,
    common.pimII,
    common.cribII,
    common.otherSeverity,
    common.otherSeverityScore,
    patient.dead,
    patient.deathDate,
    common.ecmo,
    common.intubated,
    common.intubStart,
    common.reintubated,
    common.intubEnd,
    ...bact.all.v6.exp,
    ...pneu.all.exp.v3,
    ...bact.sep.v3
  ],

  v6: [
    multi.expManagment,
    patient.sheetNumber.v2,
    common.uf,
    common.ufSep,
    common.unitType.v2,
    sepUnitType,
    patient.birthdate,
    patient.sex,
    patient.entrydate,
    common.immuno,
    common.cancer,
    patient.antibio,
    patient.portageBmrBhre,
    patient.bmrBhreMicroOrg.v2,
    common.mix,
    common.origin.v2,
    common.bmi,
    common.neoOrigin,
    common.birthSite,
    common.birthWeight,
    common.gestationalAge,
    common.neoVentilation,
    common.leucomalacy,
    common.trauma,
    common.diagnosis,
    common.igsII,
    common.pimII,
    common.cribII,
    common.otherSeverity,
    common.otherSeverityScore,
    patient.dead,
    patient.deathDate,
    common.ecmo,
    common.intubated,
    common.intubStart,
    common.reintubated,
    common.intubEnd,
    ...bact.all.v7.exp,
    ...pneu.all.exp.v4,
    ...bact.sep.v3
  ],

  v7: [
    multi.expManagment,
    patient.sheetNumber.v2,
    common.uf,
    common.ufSep,
    common.unitType.v2,
    sepUnitType,
    patient.birthdate,
    patient.sex,
    patient.entrydate,
    common.immuno,
    common.cancer,
    patient.antibio,
    patient.portageBmrBhre,
    patient.bmrBhreMicroOrg.v2,
    common.mix,
    common.origin.v2,
    common.bmi,
    common.neoOrigin,
    common.birthSite,
    common.birthWeight,
    common.gestationalAge,
    common.neoVentilation,
    common.leucomalacy,
    common.trauma,
    common.diagnosis,
    common.igsII,
    common.pimII,
    common.cribII,
    common.otherSeverity,
    common.otherSeverityScore,
    patient.dead,
    patient.deathDate,
    common.ecmo,
    common.intubated,
    common.intubStart,
    common.reintubated,
    common.intubEnd,
    ...bact.all.v8.exp,
    ...pneu.all.exp.v4,
    ...bact.sep.v5
  ]
}

function build (fields, nb) {
  return {
    titles: titles,
    repeatableTitles: repeatableTitles,

    fields: fields,

    buildNotes: function (result, sheet, surveillance) {
      nb(result, sheet, surveillance)
      return result
    }
  }
}

export default {
  '2026': build(expFields.v7, notes.build2026),
  '2025': build(expFields.v6, notes.build2025),
  '2024': build(expFields.v5, notes.build2024),
  '2023': build(expFields.v4, notes.build2023),
  '2022': build(expFields.v3, notes.build2022),
  '2021': build(expFields.v2, notes.build2021),
  '2020': build(expFields.v1, notes.build2020)
}
